import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const catalogueImages = [
  { webp: "/lovable-uploads/26661aa1-optimized.webp", fallback: "/lovable-uploads/26661aa1-d651-4fc6-990b-086694595aa6.png" }, // 1. MAGIC DOOR
  { webp: "/lovable-uploads/69d970a9-5613-429c-b6df-860f63568512.webp", fallback: "/lovable-uploads/69d970a9-5613-429c-b6df-860f63568512.png" }, // 2. UȘI
  { webp: "/lovable-uploads/7e6500ea-e8f3-425f-8c80-c098c12c2aa6.webp", fallback: "/lovable-uploads/7e6500ea-e8f3-425f-8c80-c098c12c2aa6.png" }, // 3. MOBILĂ LA COMANDĂ
  { webp: "/lovable-uploads/a37a74a4-54ae-4dca-89e7-a7de98808c4d.webp", fallback: "/lovable-uploads/a37a74a4-54ae-4dca-89e7-a7de98808c4d.png" }, // 4. PARDOSEALĂ ȘI SCĂRI
  { webp: "/lovable-uploads/ccdb332d-0ab5-4b17-a2d0-7ebefe96b925.webp", fallback: "/lovable-uploads/ccdb332d-0ab5-4b17-a2d0-7ebefe96b925.png" }, // 5. TÂMPLĂRIE
  { webp: "/lovable-uploads/4964dd46-optimized.webp", fallback: "/lovable-uploads/4964dd46-aa1a-4fae-8c48-9b68bb32d4ba.png" }, // 6. est. 1999
];

export default function PageFlipCatalogue() {
  const [currentPage, setCurrentPage] = useState(0);
  const [isFlipping, setIsFlipping] = useState(false);
  const [flipDirection, setFlipDirection] = useState<'next' | 'prev'>('next');
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);
  const { t } = useLanguage();

  const nextPage = () => {
    if (isFlipping || currentPage >= catalogueImages.length - 1) return;
    setIsFlipping(true);
    setFlipDirection('next');
    setTimeout(() => {
      setCurrentPage(prev => prev + 1);
      setIsFlipping(false);
    }, 300);
  };

  const prevPage = () => {
    if (isFlipping || currentPage <= 0) return;
    setIsFlipping(true);
    setFlipDirection('prev');
    setTimeout(() => {
      setCurrentPage(prev => prev - 1);
      setIsFlipping(false);
    }, 300);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    
    const distance = touchStartX.current - touchEndX.current;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextPage();
    } else if (isRightSwipe) {
      prevPage();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') nextPage();
      if (e.key === 'ArrowLeft') prevPage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, isFlipping]);

  return (
    <section className="py-16 bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-4">
        <div className="sticky top-4 z-20 mb-12">
          <h2 className="text-xl md:text-2xl font-light text-left tracking-wide bg-background/95 backdrop-blur-sm py-2 px-4 rounded-lg inline-block">
            {t('catalogue.title')}
          </h2>
        </div>
        
        <div className="relative max-w-3xl mx-auto">
          {/* Book Container */}
          <div 
            className="relative perspective-1000"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="relative w-full aspect-[3/4] mx-auto max-w-xs md:max-w-sm lg:max-w-md">
              {/* Shadow */}
              <div className="absolute inset-0 bg-black/10 blur-xl transform translate-y-4 rounded-lg" />
              
              {/* Book Pages */}
              <div className="relative w-full h-full rounded-lg overflow-hidden shadow-2xl border-2 border-muted">
                {catalogueImages.map((image, index) => (
                  <div
                    key={index}
                    className={`
                      absolute inset-0 transition-all duration-500 ease-in-out
                      ${index === currentPage ? 'opacity-100 z-10' : 'opacity-0 z-0'}
                      ${isFlipping && index === currentPage && flipDirection === 'next' 
                        ? 'transform rotateY-180 origin-left' 
                        : ''}
                      ${isFlipping && index === currentPage - 1 && flipDirection === 'prev'
                        ? 'transform rotateY-0 origin-right'
                        : ''}
                    `}
                    style={{
                      transformStyle: 'preserve-3d',
                      backfaceVisibility: 'hidden'
                    }}
                  >
                    <picture>
                      <source srcSet={image.webp} type="image/webp" />
                      <img
                        src={image.fallback}
                        alt={`Catalogue page ${index + 1}`}
                        className="w-full h-full object-contain rounded-lg"
                        draggable={false}
                        loading={index <= 1 ? "eager" : "lazy"}
                      />
                    </picture>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation Controls */}
          <button
            onClick={prevPage}
            disabled={currentPage === 0 || isFlipping}
            aria-label="Previous page"
            className={`
              absolute left-4 top-1/2 -translate-y-1/2 z-20
              w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm shadow-lg
              flex items-center justify-center transition-all duration-200
              ${currentPage === 0 || isFlipping 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:bg-white hover:scale-110 active:scale-95'}
            `}
          >
            <ChevronLeft className="w-6 h-6 text-primary" />
          </button>

          <button
            onClick={nextPage}
            disabled={currentPage >= catalogueImages.length - 1 || isFlipping}
            aria-label="Next page"
            className={`
              absolute right-4 top-1/2 -translate-y-1/2 z-20
              w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm shadow-lg
              flex items-center justify-center transition-all duration-200
              ${currentPage >= catalogueImages.length - 1 || isFlipping
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-white hover:scale-110 active:scale-95'}
            `}
          >
            <ChevronRight className="w-6 h-6 text-primary" />
          </button>

          {/* Page Indicators */}
          <div className="flex justify-center space-x-2 mt-8">
            {catalogueImages.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  if (!isFlipping && index !== currentPage) {
                    setIsFlipping(true);
                    setFlipDirection(index > currentPage ? 'next' : 'prev');
                    setTimeout(() => {
                      setCurrentPage(index);
                      setIsFlipping(false);
                    }, 300);
                  }
                }}
                disabled={isFlipping}
                aria-label={`Go to page ${index + 1}`}
                aria-current={index === currentPage ? 'true' : 'false'}
                className={`
                  w-3 h-3 rounded-full transition-all duration-300
                  ${index === currentPage 
                    ? 'bg-primary scale-125' 
                    : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'}
                `}
              />
            ))}
          </div>

        </div>

        {/* Instructions */}
        <div className="text-center mt-8 text-sm text-muted-foreground max-w-md mx-auto">
          <p>{t('catalogue.instructions')}</p>
        </div>
      </div>
    </section>
  );
}